import {
  BookOpen,
  Cards,
  Exam,
  FileText,
  FilmStrip,
  Question,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import type { ChatContextLabel as ChatContextLabelValue } from '@/types/chatStream';
import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';

interface ChatContextLabelProps {
  context: ChatContextLabelValue;
  /**
   * The message is in this QBank block's one chat (ORA-1285). There the label
   * names the question by its place in the block, since the thread holds
   * several; anywhere else it keeps the server's own label.
   */
  inBlockThread?: boolean;
}

/** Shared with the composer's context pills, so the pill and the badge match. */
export const CONTEXT_ICON = {
  question: Question,
  flashcard: Cards,
  lecture: FileText,
  video: FilmStrip,
  article: BookOpen,
  exam: Exam,
} as const;

const CONTEXT_TYPE_LABEL = {
  question: 'Question',
  flashcard: 'Flashcard',
  lecture: 'Document',
  video: 'Video',
  article: 'Article',
  exam: 'Exam',
} as const;

/**
 * The safe context snapshot that belongs to one user message. It deliberately
 * knows nothing about the live route: a later card flip or page change cannot
 * rewrite the context the student actually asked Ora about. For the same
 * reason a lecture label opens only the lecture it names itself (ORA-736),
 * never whatever the page shows now.
 */
const ChatContextLabel = ({
  context,
  inBlockThread = false,
}: ChatContextLabelProps) => {
  const { open } = useChatDocumentViewer();
  const Icon = CONTEXT_ICON[context.kind];
  const typeLabel = CONTEXT_TYPE_LABEL[context.kind];

  const label =
    inBlockThread && context.blockPosition
      ? `Question ${context.blockPosition.index}`
      : context.label.toLocaleLowerCase() === typeLabel.toLocaleLowerCase()
        ? typeLabel
        : `${typeLabel} · ${context.label}`;

  const lectureId = context.kind === 'lecture' ? context.lectureId : undefined;

  const content = (
    <>
      <Icon size={12} aria-hidden="true" />
      <span className="min-w-0 truncate">{label}</span>
    </>
  );

  if (lectureId) {
    return (
      <Badge
        asChild
        variant="chip-muted"
        className="mb-1 max-w-[85%] cursor-pointer"
      >
        <button
          type="button"
          aria-label={`Open context: ${label}`}
          title={label}
          data-testid="chat-message-context"
          onClick={() => open({ kind: 'lecture', lectureId })}
        >
          {content}
        </button>
      </Badge>
    );
  }

  return (
    <Badge
      variant="chip-muted"
      aria-label={`Context: ${label}`}
      title={label}
      className="mb-1 max-w-[85%]"
      data-testid="chat-message-context"
    >
      {content}
    </Badge>
  );
};

export default ChatContextLabel;
